import React from 'react';
import {
    Title,
    Text,
    Divider,
    Tooltip,
    Stack,
    Flex,
    Box,
    Skeleton,
} from '@mantine/core';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';

import { Request } from '../../codegen/graphql';
import formatCurrency from '../../utility/formatCurrency';
import { GET_SAP_CONFIRMED_PRICES_FOR_REQUEST } from '../../apollo/queries/priceChangeRequest';
import { When } from '../common';
import { pricesDiffer } from '../../helpers/sapOutcomeHelpers';

type Props = {
    flatRequest: Request | null;
};

type FinalPriceBlockProps = {
    label: string;
    requestedPrice: number;
    sapConfirmedPrice: number | null;
    currency: string;
    validFrom?: string | null;
    validTo?: string | null;
    isSapPricePending: boolean;
    sapPriceLoadFailed: boolean;
};

function FinalPriceBlock({
    label,
    requestedPrice,
    sapConfirmedPrice,
    currency,
    validFrom,
    validTo,
    isSapPricePending,
    sapPriceLoadFailed,
}: FinalPriceBlockProps) {
    const hasMismatch =
        !isSapPricePending &&
        !sapPriceLoadFailed &&
        sapConfirmedPrice != null &&
        pricesDiffer(sapConfirmedPrice, requestedPrice);
    const displayPrice = hasMismatch ? sapConfirmedPrice : requestedPrice;

    return (
        <Stack gap={8}>
            <Text className="uppercase text-xs text-[#7D8FB3] font-bold">
                {label}
            </Text>
            <Flex>
                <Box className="flex-1 p-4">
                    <Text className="uppercase text-xs text-[#7D8FB3] font-bold">
                        Final Price
                    </Text>
                    <When condition={isSapPricePending}>
                        <Skeleton height={22} width={110} className="mt-1" />
                    </When>
                    <When condition={!isSapPricePending}>
                        <Text className="font-bold text-primary">
                            {formatCurrency(displayPrice, currency)}
                        </Text>
                    </When>
                    <When condition={hasMismatch}>
                        <Tooltip
                            label="SAP applied a different price on implementation"
                            openDelay={300}
                            zIndex={2000}
                        >
                            <Text
                                size="xs"
                                c="orange.7"
                                fw={500}
                                className="mt-1 cursor-help"
                            >
                                was {formatCurrency(requestedPrice, currency)}
                            </Text>
                        </Tooltip>
                    </When>
                    <When condition={sapPriceLoadFailed}>
                        <Text size="xs" c="dimmed" className="mt-1">
                            SAP confirmation unavailable
                        </Text>
                    </When>
                </Box>

                <Divider orientation="vertical" color="#e6e9ef" my="xs" />

                <Box className="flex-1 p-4">
                    <Text className="uppercase text-xs text-[#7D8FB3] font-bold">
                        Price Valid From
                    </Text>
                    <Text className="font-bold text-primary">
                        {validFrom
                            ? dayjs(validFrom).format('D MMM YYYY')
                            : '—'}
                    </Text>
                </Box>

                <Divider orientation="vertical" color="#e6e9ef" my="xs" />

                <Box className="flex-1 p-4">
                    <Text className="uppercase text-xs text-[#7D8FB3] font-bold">
                        Price Valid To
                    </Text>
                    <Text className="font-bold text-primary">
                        {validTo ? dayjs(validTo).format('D MMM YYYY') : '—'}
                    </Text>
                </Box>
            </Flex>
        </Stack>
    );
}

function FinalPriceInfo({ flatRequest }: Props) {
    const {
        data: sapConfirmedData,
        loading: sapConfirmedLoading,
        error: sapConfirmedError,
    } = useQuery(GET_SAP_CONFIRMED_PRICES_FOR_REQUEST, {
        variables: { id: flatRequest?.id! },
        skip: !flatRequest?.id || !flatRequest?.completed,
        fetchPolicy: 'cache-and-network',
    });

    const hasCashbuildPrice =
        flatRequest?.cashbuildPrice !== null &&
        flatRequest?.cashbuildPrice !== undefined;
    const hasPnlPrice =
        flatRequest?.pnlPrice !== null && flatRequest?.pnlPrice !== undefined;
    if (!flatRequest?.completed || (!hasCashbuildPrice && !hasPnlPrice)) {
        return null;
    }

    const currency = flatRequest.currentPriceCurrency || 'ZAR';

    const hasSapConfirmedResponse =
        sapConfirmedData?.getPriceChangeRequest != null;
    const isSapPricePending = sapConfirmedLoading && !hasSapConfirmedResponse;
    const sapPriceLoadFailed = !!sapConfirmedError && !hasSapConfirmedResponse;

    const sapConfirmedCashbuildPrice =
        sapConfirmedData?.getPriceChangeRequest?.sapConfirmedCashbuildPrice ??
        null;
    const sapConfirmedPnlPrice =
        sapConfirmedData?.getPriceChangeRequest?.sapConfirmedPnlPrice ?? null;

    return (
        <Stack
            gap="md"
            className="flex-1 shadow-sm bg-white px-5 py-4 rounded-lg mb-5"
        >
            <Stack gap={4}>
                <Title order={4}>Final Approved Prices</Title>
                <Text c="dimmed" size="sm">
                    Prices approved by supplier and applied to stores.
                </Text>
            </Stack>

            <When condition={hasCashbuildPrice}>
                <FinalPriceBlock
                    label="FINAL CASHBUILD PRICE"
                    requestedPrice={flatRequest.cashbuildPrice || 0}
                    sapConfirmedPrice={sapConfirmedCashbuildPrice}
                    currency={currency}
                    validFrom={flatRequest.changedPriceValidFrom}
                    validTo={flatRequest.changedPriceValidTo}
                    isSapPricePending={isSapPricePending}
                    sapPriceLoadFailed={sapPriceLoadFailed}
                />
            </When>

            <When condition={hasPnlPrice}>
                <FinalPriceBlock
                    label="FINAL P&L PRICE"
                    requestedPrice={flatRequest.pnlPrice || 0}
                    sapConfirmedPrice={sapConfirmedPnlPrice}
                    currency={currency}
                    validFrom={flatRequest.changedPriceValidFrom}
                    validTo={flatRequest.changedPriceValidTo}
                    isSapPricePending={isSapPricePending}
                    sapPriceLoadFailed={sapPriceLoadFailed}
                />
            </When>
        </Stack>
    );
}

export default FinalPriceInfo;
